import * as React from "react";
import states from "states-us";
import { map } from "lodash";
import { BasicFormProps } from "@/interfaces";
import { Field } from "@/components/ui";
import { Combobox } from "@/components/ui/combobox";
import { useSegmentation } from "@/utils";
import { PlayerSegmentationType } from "@/services/bedrock";
import { useEffect } from "react";

interface OwnProps extends BasicFormProps {
  placeholder?: string;
  id: string;
  type: PlayerSegmentationType;
  label: string;
  fieldValue: any;
  disabled: boolean;
  className?: string;
  multiple: boolean;
  allowAdditions: boolean;
  handleChange: (data: any) => void;
}

type Props = OwnProps;

type SegmentationOption = {
  value: string;
  label: string;
};

const SegmentationDropDownFC: React.FC<Props> = ({
  id,
  type,
  placeholder,
  fieldValue,
  label,
  disabled,
  className = "segmentationDropDown",
  multiple,
  allowAdditions,
  handleChange,
}) => {
  const { getOptions } = useSegmentation([type]);
  const [options, setOptions] = React.useState<SegmentationOption[]>([]);
  useEffect(() => {
    if (type === "STATE") {
      setOptions(
        map(states, state => ({
          value: state.abbreviation.toUpperCase(),
          label: `${state.abbreviation} - ${state.name}`,
        }))
      );
    } else {
      const newOptions = getOptions(type);
      setOptions(
        newOptions.map(option => ({
          value: option.text,
          label: option.text,
        }))
      );
    }
  }, [type, getOptions]);

  const itemsToRender = React.useMemo(() => {
    const merged = [...options];

    if (multiple) {
      const values = Array.isArray(fieldValue) ? fieldValue : [];
      values.forEach(val => {
        if (val && !merged.find(o => o.value === val)) {
          merged.unshift({ value: val, label: val }); // Temporarily inject missing initial value
        }
      });
    } else {
      if (fieldValue && !merged.find(o => o.value === fieldValue)) {
        merged.unshift({ value: fieldValue, label: fieldValue });
      }
    }

    return merged;
  }, [options, fieldValue, multiple]);

  const comboboxValue = React.useMemo(() => {
    if (multiple) {
      const values = Array.isArray(fieldValue) ? fieldValue : [];
      return values.map(val => itemsToRender.find(o => o.value === val) || { value: val, label: val });
    }

    if (!fieldValue) return null;
    return itemsToRender.find(o => o.value === fieldValue) || { value: fieldValue, label: fieldValue };
  }, [fieldValue, multiple, itemsToRender]);

  const handleValueChange = React.useCallback(
    (newValue: any) => {
      if (multiple) {
        const selectedValues = Array.isArray(newValue) ? newValue.map((v: SegmentationOption) => v.value) : [];
        handleChange({ value: selectedValues });
      } else {
        handleChange({ value: newValue?.value ?? "" });
      }
    },
    [handleChange, multiple]
  );

  const handleAddItem = React.useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) return undefined;

      const newOption: SegmentationOption = { value: trimmed, label: trimmed };
      setOptions(prev => [newOption, ...prev]);
      return newOption;
    },
    [setOptions]
  );

  return (
    <Field orientation="vertical" className="mt-2">
      <Field.Content>
        <Field.Label>{label}</Field.Label>
      </Field.Content>

      <Combobox<SegmentationOption, typeof multiple>
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        multiple={multiple}
        searchable={!multiple || allowAdditions}
        allowAdditions={allowAdditions}
        items={itemsToRender}
        value={comboboxValue}
        onValueChange={handleValueChange}
        onAddItem={handleAddItem}
        itemToStringLabel={item => item.label}
        itemToStringValue={item => item.value}
        isItemEqualToValue={(a, b) => a?.value === b?.value} // Safer equality check
        showClear={!multiple}
        data-elm-id={`segmentation_dropdown_${id}`}
      />
    </Field>
  );
};

export const SegmentationDropDown = React.memo(SegmentationDropDownFC);
